import React from 'react';
import {
  Platform,
  Text,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { cn } from '@/utils/cn';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { PressableScale } from './pressable-scale';

export type ButtonVariant =
  | 'default'
  | 'primary'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'white'
  | 'ghost'
  | 'link';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends PressableProps {
  title?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children?: React.ReactNode;
}

const containerVariants: Record<ButtonVariant, string> = {
  default: 'bg-primary hover:bg-primary/90',
  primary: 'bg-primary-500 hover:bg-primary-600',
  destructive: 'bg-error-500 hover:bg-error-600',
  outline: 'border border-neutral-200 bg-card hover:bg-neutral-50',
  secondary: 'bg-secondary-500 hover:bg-secondary-600',
  white: 'bg-card shadow-sm hover:bg-card/90 active:bg-card/85',
  ghost: 'hover:bg-primary-50',
  link: 'underline-offset-4 hover:underline',
};

const containerSizes: Record<ButtonSize, string> = {
  xs: 'h-8 min-h-[32px] px-3 rounded-full',
  sm: 'h-10 min-h-[40px] px-4 rounded-full',
  md: 'h-11 min-h-[44px] px-5 rounded-full',
  lg: 'h-12 min-h-[48px] px-8 rounded-full',
  icon: 'h-11 w-11 rounded-full',
};

const textSizes: Record<ButtonSize, string> = {
  xs: 'text-xs font-semibold',
  sm: 'text-sm font-semibold',
  md: 'text-sm font-semibold md:text-base',
  lg: 'text-base font-semibold md:text-lg',
  icon: 'text-base font-semibold',
};

const textVariants: Record<ButtonVariant, string> = {
  default: 'text-white',
  primary: 'text-white',
  destructive: 'text-white',
  outline: 'text-neutral-900',
  // Fundo amarelo da marca: texto escuro nos dois temas.
  secondary: 'text-secondary-foreground',
  white: 'text-primary',
  ghost: 'text-neutral-900',
  link: 'text-primary',
};

/** No mobile, botões lg usam a escala md — formulários e CTAs ficam proporcionais. */
function resolveButtonSize(size: ButtonSize): ButtonSize {
  if (Platform.OS === 'web') return size;
  if (size === 'lg') return 'md';
  return size;
}

function getNativeLabelStyle(
  variant: ButtonVariant,
  size: ButtonSize,
  th: (hex: string) => string,
): TextStyle {
  const fontSize = size === 'lg' ? 17 : size === 'xs' ? 13 : size === 'sm' ? 14 : 15;
  const base: TextStyle = {
    fontWeight: '600',
    fontSize,
    textAlign: 'center',
    letterSpacing: 0.1,
  };

  switch (variant) {
    case 'destructive':
    case 'default':
    case 'primary':
      return { ...base, color: '#FFFFFF' };
    case 'white':
    case 'link':
      return { ...base, color: '#FD5001' };
    case 'secondary':
      return { ...base, color: '#18181B' };
    case 'outline':
    case 'ghost':
      return { ...base, color: th('#18181B') };
    default:
      return { ...base, color: '#FFFFFF' };
  }
}

export function Button({
  title,
  variant = 'default',
  size = 'md',
  className,
  children,
  disabled,
  style,
  onPress,
  ...pressableProps
}: ButtonProps) {
  const th = useThemedHex();
  const effectiveSize = resolveButtonSize(size);
  const content = children || title;

  const containerClass = cn(
    'flex-row items-center justify-center gap-2 disabled:opacity-50',
    Platform.OS === 'web' &&
      'inline-flex transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none',
    containerVariants[variant],
    containerSizes[effectiveSize],
    className,
  );

  const textClass = cn(textVariants[variant], textSizes[effectiveSize]);
  const nativeLabelStyle =
    Platform.OS !== 'web' && typeof content === 'string'
      ? getNativeLabelStyle(variant, effectiveSize, th)
      : undefined;

  const buttonStyle: StyleProp<ViewStyle> = [
    disabled ? { opacity: 0.5 } : null,
    style as StyleProp<ViewStyle>,
  ];

  return (
    <PressableScale
      className={containerClass}
      disabled={disabled}
      style={buttonStyle}
      onPress={onPress}
      pressScale={0.96}
      haptic
      accessibilityRole="button"
      {...pressableProps}
    >
      {typeof content === 'string' ? (
        nativeLabelStyle ? (
          <Text style={nativeLabelStyle} numberOfLines={1}>
            {content}
          </Text>
        ) : (
          <Text className={textClass} numberOfLines={1}>
            {content}
          </Text>
        )
      ) : (
        content
      )}
    </PressableScale>
  );
}
